package com.sddamico.draftmachine.ds.forms

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.sddamico.draftmachine.ds.DmColor
import com.sddamico.draftmachine.ds.DmEffects
import com.sddamico.draftmachine.ds.DmMotion
import com.sddamico.draftmachine.ds.DmSpacing
import com.sddamico.draftmachine.ds.DmType
import com.sddamico.draftmachine.ds.boxShadowRaw
import com.sddamico.draftmachine.ds.displayFlex
import com.sddamico.draftmachine.ds.roleBody
import com.sddamico.draftmachine.ds.roleSmall
import com.sddamico.draftmachine.ds.transitionRaw
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.TextArea

/**
 * A multi-line recessed plate field — shares the milled-slot look of [Input].
 * Port of `components/forms/Textarea.jsx`.
 */
@Composable
fun Textarea(
  modifier: Modifier = Modifier,
  label: String? = null,
  hint: String? = null,
  error: String? = null,
  placeholder: String? = null,
  value: String? = null,
  rows: Int = 4,
  onValueChange: ((String) -> Unit)? = null,
) {
  var focused by remember { mutableStateOf(false) }
  val fieldId = slugId("ta", label)
  val borderColor = when {
    error != null -> DmColor.danger
    focused -> DmColor.brass500
    else -> DmColor.borderStrong
  }
  val shadow = if (focused) "${DmEffects.shadowInset}, ${DmEffects.ring}" else DmEffects.shadowInset

  Div(
    attrs = modifier
      .displayFlex().flexDirection(FlexDirection.Column).gap(DmSpacing.space2).fillMaxWidth()
      .toAttrs()
  ) {
    if (label != null) {
      Label(
        attrs = Modifier.roleSmall().fontWeight(DmType.weightSemibold).color(DmColor.textSecondary)
          .toAttrs { fieldId?.let { id -> attr("for", id) } }
      ) { Text(label) }
    }
    TextArea(
      attrs = Modifier
        .fillMaxWidth()
        .padding(topBottom = DmSpacing.space3, leftRight = DmSpacing.space4)
        .backgroundColor(DmColor.surfaceInset)
        .border(1.px, LineStyle.Solid, borderColor)
        .borderRadius(DmEffects.radiusSm)
        .boxShadowRaw(shadow)
        .color(DmColor.text).roleBody()
        .transitionRaw("border-color ${DmMotion.durFast}, box-shadow ${DmMotion.durFast}")
        .styleModifier { property("resize", "vertical"); property("outline", "none") }
        .toAttrs {
          fieldId?.let { attr("id", it) }
          attr("rows", rows.toString())
          if (placeholder != null) attr("placeholder", placeholder)
          if (value != null) attr("value", value)
          onInput { ev -> onValueChange?.invoke(ev.value) }
          onFocus { focused = true }
          onBlur { focused = false }
        }
    )
    if (hint != null || error != null) {
      SpanText(
        error ?: hint!!,
        Modifier.roleSmall().fontSize(DmType.textXs).color(if (error != null) DmColor.danger else DmColor.textMuted)
      )
    }
  }
}
